// WS-4 (OP-225/227) i18n — "uiux" cluster: labels introduced by the V1 UI/UX
// overhaul. Covers the shared ConfirmDialog / PromptDialog chrome, the CRM
// stage picker + free-text fallback, Conversation-States drag-reorder, the
// Knowledge "not configured" guidance banner, the Discovery score-override
// modal, and the User-list confirmation messages (previously native
// window.confirm strings). Spread into the central LABELS in labels.ts.
//
// EN values equal the pre-overhaul English so existing text-based tests stay
// green; HE values are the Hebrew translation. Placeholders like {name} are
// substituted at the call site via String.replace (useT does not interpolate).
export const UIUX_LABELS = {
  // ═══════════════════════════════════════════════════════════════════════
  //  Shared ConfirmDialog / PromptDialog chrome
  // ═══════════════════════════════════════════════════════════════════════
  confirmDialogTitle: { en: 'Please confirm', he: 'אישור פעולה' },
  confirmDialogTypeToConfirm: { en: 'Type {word} to confirm', he: 'הקלד {word} כדי לאשר' },
  promptDialogTitle: { en: 'Enter a value', he: 'הזן ערך' },

  // ═══════════════════════════════════════════════════════════════════════
  //  CRM stage picker (ConversationStates cs-crm + CrmSettings StageMap)
  // ═══════════════════════════════════════════════════════════════════════
  crmStagePipelineAria: { en: 'CRM pipeline', he: 'צינור CRM' },
  crmStageStageAria: { en: 'CRM stage', he: 'שלב CRM' },
  crmStageSelectPipeline: { en: 'Select pipeline…', he: 'בחר צינור…' },
  crmStageSelectStage: { en: 'Select stage…', he: 'בחר שלב…' },
  crmStageNone: { en: '(no stage mapped)', he: '(לא מופה שלב)' },
  // Replaced the crmStageFreeText* keys (OP-785). Typing a stage reference by hand is how a stage id
  // from another portal reached a live write, so the field is now shown read-only with the reason.
  crmStageUnavailableAria: { en: 'CRM stage reference (unavailable)', he: 'הפניית שלב CRM (לא זמין)' },
  crmStageUnavailableHint: {
    en: 'CRM pipelines could not be loaded, so a stage cannot be chosen or typed here. Check the CRM connection and run Refresh Schema, then reload this page.',
    he: 'לא ניתן היה לטעון את צינורות ה-CRM, ולכן לא ניתן לבחור או להקליד שלב כאן. בדקו את חיבור ה-CRM והריצו רענון סכימה, ואז טענו מחדש את הדף.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Conversation States — drag-and-drop reorder (replaces the numeric input)
  // ═══════════════════════════════════════════════════════════════════════
  convStatesColReorder: { en: 'Reorder', he: 'סדר' },
  convStatesReorderHandleAria: { en: 'Drag to reorder {name}', he: 'גרור כדי לשנות את הסדר של {name}' },
  convStatesReorderHint: {
    en: 'Drag a row, or focus its handle and press Space then the arrow keys, to reorder. The new order saves automatically.',
    he: 'גרור שורה, או מקד את הידית ולחץ רווח ואז מקשי חצים, כדי לשנות את הסדר. הסדר החדש נשמר אוטומטית.',
  },
  convStatesReorderSaveError: {
    en: 'Could not save the new order. Please try again.',
    he: 'לא ניתן היה לשמור את הסדר החדש. נסה שוב.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Knowledge — "not configured" guidance banner (unset SLM / embedding / OCR)
  // ═══════════════════════════════════════════════════════════════════════
  kbNotConfiguredTitle: { en: 'Knowledge features not configured', he: 'תכונות הידע אינן מוגדרות' },
  kbNotConfiguredBody: {
    en: 'This operation needs a knowledge capability (embedding, document OCR, or the summarization model) that is not configured for this environment. Contact your administrator to enable it.',
    he: 'פעולה זו דורשת יכולת ידע (הטמעה, OCR של מסמכים, או מודל התמצות) שאינה מוגדרת בסביבה זו. פנה למנהל המערכת כדי להפעיל אותה.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  Discovery — score-override modal (replaces window.prompt)
  // ═══════════════════════════════════════════════════════════════════════
  discoveryScoreOverrideTitle: { en: 'Override relevance score', he: 'עקיפת ציון רלוונטיות' },
  discoveryScoreOverrideLabel: { en: 'Relevance score (0–100)', he: 'ציון רלוונטיות (0–100)' },
  discoveryScoreOverrideRange: { en: 'Enter a whole number from 0 to 100.', he: 'הזן מספר שלם בין 0 ל-100.' },

  // ─ Discovery — P4-improvement micro-copy (badges/tooltips with no discoveryTab* key) ─
  discoveryGradeFailedScoreTitle: {
    en: 'Grading failed for this row — re-grade to retry.',
    he: 'הדירוג נכשל עבור שורה זו — דרג מחדש כדי לנסות שוב.',
  },
  discoveryGradeFailedBadge: { en: 'needs re-grade', he: 'דרוש דירוג מחדש' },
  discoveryGradeFailedBadgeTitle: {
    en: 'Grading failed for this row — use "Re-grade failed" to retry.',
    he: 'הדירוג נכשל עבור שורה זו — השתמש ב"דרג מחדש שנכשלו" כדי לנסות שוב.',
  },
  discoveryAdminBadge: { en: 'ADMIN', he: 'מנהל' },
  discoveryAdminBadgeTitle: {
    en: "Relevance score manually set by an admin — automatic re-grading won't change it.",
    he: 'ציון הרלוונטיות נקבע ידנית על ידי מנהל — דירוג מחדש אוטומטי לא ישנה אותו.',
  },
  discoveryOverrideScoreTitle: { en: 'Manually override the relevance score', he: 'עקוף ידנית את ציון הרלוונטיות' },
  discoveryEditScore: { en: '✎ edit', he: '✎ ערוך' },

  // ═══════════════════════════════════════════════════════════════════════
  //  Users — action confirmations (replace native window.confirm)
  // ═══════════════════════════════════════════════════════════════════════
  userConfirmDeactivateTitle: { en: 'Deactivate user', he: 'השבתת משתמש' },
  userConfirmDeactivate: { en: 'Deactivate {name}?', he: 'להשבית את {name}?' },
  userConfirmActivateTitle: { en: 'Activate user', he: 'הפעלת משתמש' },
  userConfirmActivate: { en: 'Activate {name}?', he: 'להפעיל את {name}?' },
  userConfirmResetPasswordTitle: { en: 'Reset password', he: 'איפוס סיסמה' },
  userConfirmResetPassword: {
    en: 'Issue a new temporary password for {name}?',
    he: 'להנפיק סיסמה זמנית חדשה עבור {name}?',
  },
  userConfirmUnlockTitle: { en: 'Unlock user', he: 'שחרור נעילת משתמש' },
  userConfirmUnlock: {
    en: 'Unlock {name}? This clears the failed-attempt lock without changing their password.',
    he: 'לשחרר את נעילת {name}? פעולה זו מנקה את נעילת הניסיונות הכושלים מבלי לשנות את הסיסמה.',
  },
  userConfirmDeleteTitle: { en: 'Delete user', he: 'מחיקת משתמש' },
  userConfirmDelete: {
    en: 'Delete {name}? Their email, password and sign-in are erased permanently and cannot be recovered; the email can then be added again as a new user. Their name stays on past tickets. To block someone temporarily, use Deactivate instead.',
    he: 'למחוק את {name}? לא ניתן לבטל פעולה זו.',
  },

  // ═══════════════════════════════════════════════════════════════════════
  //  CrmSettings — gaps not covered by the crmSettings* keys in labels.tenants
  // ═══════════════════════════════════════════════════════════════════════
  crmSettingsNoTenant: { en: 'No tenant selected.', he: 'לא נבחר ארגון.' },
  crmSettingsStubWarningTitle: { en: 'Simulated provider.', he: 'ספק מדומה.' },
  crmSettingsStubWarningBody: {
    en: '“Stub” fakes every CRM operation — exports report success and Test Connection passes, but nothing is sent to any real CRM. Select HubSpot and save a Private App token to sync tickets for real.',
    he: '"Stub" מזייף כל פעולת CRM — הייצוא מדווח על הצלחה ובדיקת החיבור עוברת, אך שום דבר אינו נשלח ל-CRM אמיתי. בחר HubSpot ושמור אסימון Private App כדי לסנכרן כרטיסים באמת.',
  },
  crmSettingsBotOwnerLabel: {
    en: 'Bot Owner ID (for bot-resolved tickets)',
    he: 'מזהה בעלים לבוט (עבור כרטיסים שנפתרו על ידי הבוט)',
  },
  crmSettingsBotOwnerHelp: {
    en: 'HubSpot requires ticket owners to be real portal users. Create/designate a "Bot" user in your portal and paste its numeric owner id here — tickets the bot resolves alone will be assigned to it instead of showing "No owner". Leave empty to keep bot-resolved tickets unowned (they are still labeled via our own "Handled By" property).',
    he: 'HubSpot דורש שבעלי כרטיסים יהיו משתמשי פורטל אמיתיים. צור/הגדר משתמש "בוט" בפורטל שלך והדבק כאן את מזהה הבעלים המספרי שלו — כרטיסים שהבוט פותר לבדו יוקצו אליו במקום להציג "ללא בעלים". השאר ריק כדי להשאיר כרטיסים שנפתרו על ידי הבוט ללא בעלים (הם עדיין מתויגים דרך המאפיין "טופל על ידי" שלנו).',
  },
  // The example MUST stay synthetic. This used to be a real owner id belonging to a real person in one
  // tenant's live portal, and it shipped to EVERY tenant admin in both locales. One tenant's portal data must
  // never appear in a shared bundle; a placeholder only has to teach the shape, and this one identifies nobody.
  crmSettingsBotOwnerPlaceholder: { en: 'e.g. 10000001', he: 'לדוגמה 10000001' },
  crmSettingsTestSimulated: {
    en: 'Simulated ({provider}) — no real CRM was contacted. Portal ID: {id}',
    he: 'מדומה ({provider}) — לא נוצר קשר עם CRM אמיתי. מזהה פורטל: {id}',
  },
} satisfies Record<string, { en: string; he: string }>;
